import { joinPaths } from '@pi/path-utils';

import type { StringNodeT } from 'src/nodes/value/StringNode';
import type { Expression, PrimitiveValue } from 'src/types';

type ItemIterationOptions = {
    items: Expression | PrimitiveValue | any[] | Record<string, any>;
    itemName: StringNodeT;
    context: any;
    callback: (options: {
        sourcePath: string;
        key: string | number;
        itemName: string;
        value: any;
    }) => void;
};

async function iterateExpressionItems({
    items,
    itemName,
    context,
    callback,
}: ItemIterationOptions): Promise<void> {
    const name = await context.evaluate(itemName);
    const target = Array.isArray(items)
        ? items // Use-case for passing in an already evaluated list
        : await context.evaluate(items);

    if (target == null || typeof target !== 'object') return;

    const entries = Array.isArray(target) ? target.map((v, i) => [i, v]) : Object.entries(target);

    // Hoist loop-invariant work out of the per-item loop: the scoped-var key names, the item
    // total, and the base path all depend only on `name`/`items`, not the individual item.
    const total = entries.length;
    const indexKey = `${name}_index`;
    const keyKey = `${name}_key`;
    const totalKey = `${name}_total`;
    const itemsIsString = typeof items === 'string';
    const basePath = itemsIsString ? (items as string).replace(/^\$\$?/, '') : '';

    for (const [key, value] of entries) {
        const path = itemsIsString ? joinPaths(basePath, key) : '';
        await context.variableBlock({
            path,
            variables: {
                [name]: value,
                [indexKey]: key,
                [keyKey]: key,
                [totalKey]: total,
            },
            callback: (sourcePath: string) => callback({ sourcePath, key, value, itemName: name }),
        });
    }
}

export default iterateExpressionItems;
